// src/pages/maintenance/KnowledgeChatTab.js
// Machine Knowledge Assistant — chat that reads the asset's uploaded manual (when present) and
// falls back to web search. See docs/MAINTENANCE_MODULE.md (Maintenance AI Addendum section) and
// backend/services/maintenanceAiService.js's askAboutAsset().
import React, { useState, useEffect, useRef } from 'react';
import maintenanceService from '../../services/maintenanceService';
import AiProgressIndicator from './AiProgressIndicator';
import MarkdownMessage from '../../components/MarkdownMessage';

const CHAT_STAGES = ['Reading the manual…', 'Thinking…', 'Composing an answer…'];

export default function KnowledgeChatTab({ asset }) {
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    maintenanceService.getChatHistory(asset.id, sessionId).then(setMessages).catch(() => setMessages([]));
  }, [asset.id, sessionId]);

  // Manual-grounded example questions (cached server-side per asset) — avoids suggesting
  // something like "What does error code E4 mean?" for a machine whose manual never mentions E4.
  useEffect(() => {
    maintenanceService.getSuggestedQuestions(asset.id)
      .then(data => setSuggestedQuestions(data.questions || []))
      .catch(() => setSuggestedQuestions([]));
  }, [asset.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (text) => {
    const q = (text ?? question).trim();
    if (!q || sending) return;
    setError('');
    setSending(true);
    setMessages(prev => [...prev, { role: 'user', content: q, createdAt: new Date().toISOString() }]);
    setQuestion('');
    try {
      const data = await maintenanceService.sendChatMessage(asset.id, q, sessionId);
      setMessages(prev => [...prev, {
        role: 'assistant', content: data.answer, usedManual: data.usedManual, usedWebSearch: data.usedWebSearch,
        createdAt: new Date().toISOString(),
      }]);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to get an answer.');
    } finally {
      setSending(false);
    }
  };

  const newChat = () => {
    setSessionId(crypto.randomUUID());
    setMessages([]);
    setError('');
  };

  return (
    <div className="card">
      <div className="card-body d-flex flex-column" style={{ minHeight: 480 }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="card-title mb-0">Ask AI — {asset.name}</h6>
          <button className="btn btn-sm btn-outline-secondary" onClick={newChat}>New Chat</button>
        </div>

        <div className="flex-grow-1 overflow-auto border rounded p-2 mb-2" style={{ minHeight: 320, maxHeight: 480, background: '#fafafa' }}>
          {messages.length === 0 && (
            <div className="text-center text-muted py-4" style={{ fontSize: 13 }}>
              <p>Ask a question about this machine — error codes, troubleshooting, maintenance procedures, parts, or specifications.</p>
              {suggestedQuestions.length > 0 && (
                <div className="d-flex flex-wrap gap-2 justify-content-center mt-2">
                  {suggestedQuestions.map(q => (
                    <button key={q} className="btn btn-sm btn-outline-primary" onClick={() => send(q)}>{q}</button>
                  ))}
                </div>
              )}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`d-flex mb-2 ${m.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
              <div
                className={`p-2 rounded ${m.role === 'user' ? 'bg-primary text-white' : 'bg-white border'}`}
                style={{ maxWidth: '80%', fontSize: 13, whiteSpace: m.role === 'user' ? 'pre-wrap' : 'normal' }}
              >
                {m.role === 'user' ? m.content : <MarkdownMessage content={m.content} style={{ fontSize: 13 }} />}
                {m.role === 'assistant' && (m.usedManual || m.usedWebSearch) && (
                  <div className="mt-1">
                    {m.usedManual && <span className="badge bg-secondary-subtle text-secondary me-1" style={{ fontSize: 10 }}>📄 manual</span>}
                    {m.usedWebSearch && <span className="badge bg-info-subtle text-info" style={{ fontSize: 10 }}>🌐 web</span>}
                  </div>
                )}
              </div>
            </div>
          ))}
          <AiProgressIndicator active={sending} messages={CHAT_STAGES} />
          <div ref={bottomRef} />
        </div>

        {error && <div className="alert alert-danger py-2 mb-2" style={{ fontSize: 13 }}>{error}</div>}

        <div className="input-group">
          <input
            className="form-control"
            placeholder="Ask about this machine…"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') send(); }}
            disabled={sending}
          />
          <button className="btn btn-primary" disabled={sending || !question.trim()} onClick={() => send()}>Send</button>
        </div>

        <div className="text-muted mt-2" style={{ fontSize: 11 }}>
          AI-generated answers may be incomplete or incorrect. Always verify safety-critical procedures (electrical, gas, refrigerant) against the official manual or a qualified technician before performing them.
        </div>
      </div>
    </div>
  );
}
